import { Injectable } from '@angular/core';
import { BehaviorSubject, finalize, from, mapTo, Observable, tap } from 'rxjs';
import { LocalStorageSettingsService } from './local-storage-settings.service';
import { ReportlinksserviceService } from 'src/app/user/reportlinks/reportlinksservice.service';
import { routes } from 'src/app/user/user-routing/user-routing.module';

@Injectable({
  providedIn: 'root'
})
export class ScreenPermissionServiceService {

  /* ================= STATE ================= */

  private allowedIds$ = new BehaviorSubject<Set<number>>(new Set());
  private criticalMap$ = new BehaviorSubject<Map<number, string>>(new Map());

  private loadingSubject = new BehaviorSubject<boolean>(false);
  loading$ = this.loadingSubject.asObservable();

  private loadingStates = new Map<string, boolean>();

  /**
   * Cache per screenPath
   * allowed → Set<buttonId>
   * critical → Map<buttonId, lookupCode>
   */
  private cache = new Map<
    string,
    { allowed: Set<number>; critical: Map<number, string> }
  >();

  constructor(
    private reportLinkService: ReportlinksserviceService,
    private localStorage: LocalStorageSettingsService
  ) {}

  /* ================= LOAD ================= */

  

load(screenPath: string): Observable<void> {
    const userId = String(this.localStorage.getSettings('UserId') ?? '');

    return from(this.reportLinkService.screenoperationspermissions(screenPath, userId))
      .pipe(
        tap((res: any[]) => {
          const allowedSet = new Set<number>();
          const criticalMap = new Map<number, string>();

          res.forEach(row => {
            const btnId = Number(row.buttonId);
            if (!Number.isFinite(btnId)) return;
            allowedSet.add(btnId);
            if (row.criticaloperation) {
              criticalMap.set(btnId, String(row.criticaloperation));
            }
          });

          this.allowedIds$.next(allowedSet);
          this.criticalMap$.next(criticalMap);
          this.cache.set(screenPath, { allowed: allowedSet, critical: criticalMap });
        }),
        finalize(() => this.finishLoading(screenPath)),
        mapTo(void 0) // <-- this ensures the Observable emits 'void'
      );
  }


  /* ================= PUBLIC API ================= */

  changes(): Observable<Set<number>> {
    return this.allowedIds$.asObservable();
  }

  getCriticalButtons(): Observable<Map<number, string>> {
    return this.criticalMap$.asObservable();
  }

  has(buttonId: number): boolean {
    return this.allowedIds$.value.has(buttonId);
  }

  isLoading(screenPath?: string): boolean {
    return screenPath
      ? this.loadingStates.get(screenPath) ?? false
      : this.loadingSubject.value;
  }

  clearCache(): void {
    this.cache.clear();
    this.resetState();
  }

  /* ================= HELPERS ================= */

  private resetState(): void {
    this.allowedIds$.next(new Set());
    this.criticalMap$.next(new Map());
  }

  private finishLoading(screenPath: string): void {
    this.loadingStates.set(screenPath, false);
    this.loadingSubject.next(false);
  }

  /* ================= DEBUG / UTIL ================= */

  print(componentName: string): string | void {
    for (const route of routes) {
      if (!route.children) continue;

      for (const child of route.children) {
        if (child.component?.name === componentName) {
          console.log('Found component path:', child.path);
          return child.path;
        }
      }
    }
    console.log('Component not found');
  }
}
